import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API_BASE, pickErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LANG_KEY = "saarthi.testLang";

const LIKERT = [
  { value: 1, label: "Strongly Disagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Agree" },
  { value: 5, label: "Strongly Agree" },
];

const LIKERT_HI = [
  { value: 1, label: "पूर्णतः असहमत" },
  { value: 2, label: "असहमत" },
  { value: 3, label: "तटस्थ" },
  { value: 4, label: "सहमत" },
  { value: 5, label: "पूर्णतः सहमत" },
];

function toAbsolute(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url.replace(/^\/api/, "")}`;
}

export default function TestPage() {
  const { categoryId } = useParams();
  const { student } = useAuth();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // {question_id: 1..5}

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null); // {message, result:{traits}}
  const [lang, setLangState] = useState(() => {
    try {
      const v = localStorage.getItem(LANG_KEY);
      return v === "hi" ? "hi" : "en";
    } catch {
      return "en";
    }
  }); // "en" | "hi"

  const setLang = useCallback((next) => {
    setLangState(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      /* ignore quota / privacy mode */
    }
  }, []);

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");
  // Save-progress autosave indicator (no UI noise — just a small tag).
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);

  const studentId = student?.student_id;
  const studentDob = student?.dob;

  const total = questions.length;
  const current = questions[index];
  const answered = current ? answers[current.id] : null;
  const allAnswered = useMemo(
    () => total > 0 && questions.every((q) => answers[q.id]),
    [questions, answers, total]
  );

  const load = useCallback(async () => {
    if (!studentId || !categoryId) return;
    setLoading(true);
    setLoadError("");
    try {
      // 1. confirm assignment + status
      const tests = await api.get(`/student/${studentId}/tests`);
      const myTest = (Array.isArray(tests.data) ? tests.data : []).find(
        (t) => t.category_id === categoryId
      );
      if (!myTest) {
        throw new Error("This test is not assigned to you.");
      }
      const completed = myTest.status === "completed";
      setAlreadyCompleted(completed);

      // 2. fetch questions for this category and the category name
      const [qs, cats] = await Promise.all([
        api.get("/questions"),
        api.get("/categories"),
      ]);
      const filtered = (Array.isArray(qs.data) ? qs.data : []).filter(
        (q) => q.category_id === categoryId
      );
      setQuestions(filtered);
      const cat = (Array.isArray(cats.data) ? cats.data : []).find(
        (c) => c.id === categoryId
      );
      setCategoryName(cat?.name || "Test");

      // 3. mark as started if not already finished, and resume from
      //    last answered question.
      if (!completed) {
        try {
          await api.post("/start-test", {
            student_id: studentId,
            category_id: categoryId,
          });
        } catch {
          /* best-effort: backend may already have it started */
        }
        try {
          const { data: saved } = await api.get(
            `/student/${studentId}/answers`,
            { params: { category_id: categoryId } }
          );
          if (Array.isArray(saved) && saved.length) {
            const map = {};
            for (const a of saved) {
              if (a.question_id) map[a.question_id] = a.selected_value;
            }
            setAnswers(map);
            // Resume from first unanswered, else stay at the last one.
            const firstUnanswered = filtered.findIndex(
              (q) => !map[q.id]
            );
            setIndex(
              firstUnanswered === -1
                ? Math.max(0, filtered.length - 1)
                : firstUnanswered
            );
          }
        } catch {
          /* best-effort: never block test load on resume hydration */
        }
      }
    } catch (err) {
      setLoadError(pickErr(err, "Failed to load this test"));
    } finally {
      setLoading(false);
    }
  }, [studentId, categoryId]);

  useEffect(() => {
    load();
  }, [load]);

  function selectAnswer(value) {
    if (!current) return;
    const qid = current.id;
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    // Fire-and-forget autosave: small payload, idempotent on the server.
    // We intentionally do not block the UI nor surface transient errors —
    // the next selection or final Submit will retry the same write.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api
        .post("/save-progress", {
          student_id: studentId,
          category_id: categoryId,
          answers: [{ question_id: qid, selected_value: value }],
        })
        .then(() => setSavedAt(Date.now()))
        .catch(() => {
          /* offline / network blip — local state still has the answer */
        });
    }, 150);
  }

  function goPrev() {
    setIndex((i) => Math.max(0, i - 1));
  }

  function goNext() {
    if (!answered) return;
    setIndex((i) => Math.min(total - 1, i + 1));
  }

  async function onSubmit() {
    if (!allAnswered || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        student_id: studentId,
        category_id: categoryId,
        answers: questions.map((q) => ({
          question_id: q.id,
          selected_value: answers[q.id],
        })),
      };
      const { data } = await api.post("/submit-answers", payload);
      setResult(data);
      setAlreadyCompleted(true);
    } catch (err) {
      setSubmitError(pickErr(err, "Failed to submit your answers"));
    } finally {
      setSubmitting(false);
    }
  }

  async function downloadReport() {
    setPdfError("");
    setPdfBusy(true);
    try {
      const { data } = await api.post("/report", {
        student_id: studentId,
        category_id: categoryId,
        student_dob: studentDob,
      });
      window.open(toAbsolute(data.pdf_url), "_blank", "noopener,noreferrer");
    } catch (err) {
      setPdfError(pickErr(err, "Failed to generate report"));
    } finally {
      setPdfBusy(false);
    }
  }

  // ----- render branches -----

  if (loading) {
    return (
      <Shell title="Loading…">
        <p className="text-sm text-slate-500">Loading test…</p>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell title="Test">
        <p data-testid="test-load-error" className="text-sm text-red-600">
          {loadError}
        </p>
        <button
          onClick={() => navigate("/student")}
          className="mt-4 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
        >
          ← Back to dashboard
        </button>
      </Shell>
    );
  }

  // Already completed and not just submitted: show summary + report.
  if (alreadyCompleted && !result) {
    return (
      <Shell title={categoryName} subtitle="Test already completed">
        <p className="text-sm text-slate-600 mb-4">
          You have already submitted this test.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={downloadReport}
            disabled={pdfBusy}
            data-testid="test-download-pdf"
            className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
          >
            {pdfBusy ? "Opening…" : "Download PDF Report"}
          </button>
          <button
            onClick={() => navigate("/student")}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            ← Back
          </button>
        </div>
        {pdfError ? (
          <p data-testid="test-pdf-error" className="mt-3 text-sm text-red-600">
            {pdfError}
          </p>
        ) : null}
      </Shell>
    );
  }

  // Just submitted: show result table + PDF.
  if (result) {
    const traits = result?.result?.traits || [];
    return (
      <Shell title={categoryName} subtitle="Result">
        <div
          data-testid="test-submit-success"
          className="mb-5 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
        >
          {result.message || "Test submitted successfully."}
        </div>

        {traits.length ? (
          <div
            className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-5"
            data-testid="test-result-table"
          >
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-4 py-2 font-medium">Trait</th>
                  <th className="px-4 py-2 font-medium text-right">Score</th>
                  <th className="px-4 py-2 font-medium">Level</th>
                </tr>
              </thead>
              <tbody>
                {traits.map((t) => (
                  <tr key={t.name} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-900">{t.name}</td>
                    <td className="px-4 py-2 text-slate-700 text-right tabular-nums">
                      {t.score}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{t.level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No scoring data returned.</p>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={downloadReport}
            disabled={pdfBusy}
            data-testid="test-download-pdf"
            className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
          >
            {pdfBusy ? "Opening…" : "Download PDF Report"}
          </button>
          <button
            onClick={() => navigate("/student")}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            Back to dashboard
          </button>
        </div>
        {pdfError ? (
          <p data-testid="test-pdf-error" className="mt-3 text-sm text-red-600">
            {pdfError}
          </p>
        ) : null}
      </Shell>
    );
  }

  // No questions for this category yet.
  if (total === 0) {
    return (
      <Shell title={categoryName}>
        <p className="text-sm text-slate-600">
          No questions are available for this test yet. Please contact your
          school admin.
        </p>
        <button
          onClick={() => navigate("/student")}
          className="mt-4 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
        >
          ← Back to dashboard
        </button>
      </Shell>
    );
  }

  // Active quiz.
  const isLast = index === total - 1;
  const answeredCount = questions.filter((q) => answers[q.id]).length;
  const progressPct = total ? Math.round((answeredCount / total) * 100) : 0;

  // Resolve display text + options for the current question, with a strict
  // English fallback when a Hindi translation is missing.
  const displayText =
    lang === "hi" && current.text_hi ? current.text_hi : current.text;
  const hasMcq = current.options && current.options.length > 0;
  const optionsForRender = hasMcq
    ? (lang === "hi" &&
       current.options_hi &&
       current.options_hi.length === current.options.length
        ? current.options_hi
        : current.options
      ).map((label, i) => ({ value: i + 1, label }))
    : lang === "hi"
      ? LIKERT_HI
      : LIKERT;

  return (
    <Shell title={categoryName} subtitle={`Question ${index + 1} of ${total}`} wide>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start">
        {/* ---- Main question card ---- */}
        <div>
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
            {/* language toggle + progress bar */}
            <div className="flex items-center justify-end mb-3" data-testid="test-language-toggle">
              <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  data-testid="test-lang-en"
                  className={
                    "px-3 py-1 transition " +
                    (lang === "en"
                      ? "bg-slate-900 text-white"
                      : "bg-white text-slate-700 hover:bg-slate-100")
                  }
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setLang("hi")}
                  data-testid="test-lang-hi"
                  className={
                    "px-3 py-1 transition border-l border-slate-300 " +
                    (lang === "hi"
                      ? "bg-slate-900 text-white"
                      : "bg-white text-slate-700 hover:bg-slate-100")
                  }
                >
                  हिन्दी
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between mb-2">
              <p
                className="text-xs font-medium text-slate-600 tabular-nums"
                data-testid="test-progress-label"
              >
                Question {index + 1} of {total}
                <span className="ml-2 text-slate-400">·</span>
                <span
                  className="ml-2 text-slate-900"
                  data-testid="test-progress-percent"
                >
                  {progressPct}% Completed
                </span>
                {savedAt ? (
                  <span
                    className="ml-3 text-emerald-600 text-[10px]"
                    data-testid="test-saved-indicator"
                  >
                    ✓ Saved
                  </span>
                ) : null}
              </p>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full mb-5 overflow-hidden">
              <div
                className="h-full bg-slate-900 transition-all"
                data-testid="test-progress-bar"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            <p
              className="text-base sm:text-lg text-slate-900 font-medium leading-relaxed mb-5"
              data-testid="test-question-text"
            >
              {displayText}
            </p>

            <div className="space-y-2">
              {optionsForRender.map((opt) => {
                const selected = answered === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => selectAnswer(opt.value)}
                    data-testid={`test-likert-${opt.value}`}
                    className={
                      "w-full text-left rounded-md border px-4 py-3 text-sm transition " +
                      (selected
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white text-slate-800 hover:bg-slate-100")
                    }
                  >
                    <span className="font-mono mr-3 opacity-80">{opt.value}</span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              onClick={goPrev}
              disabled={index === 0}
              data-testid="test-prev"
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              ← Previous
            </button>

            {isLast ? (
              <button
                onClick={onSubmit}
                disabled={!allAnswered || submitting}
                data-testid="test-submit"
                title={
                  !allAnswered
                    ? "Answer every question before submitting"
                    : undefined
                }
                className="rounded-md bg-slate-900 text-white text-sm font-medium px-5 py-2 hover:bg-slate-800 disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            ) : (
              <button
                onClick={goNext}
                disabled={!answered}
                data-testid="test-next"
                className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
              >
                Next →
              </button>
            )}
          </div>

          {submitError ? (
            <p data-testid="test-submit-error" className="mt-3 text-sm text-red-600">
              {submitError}
            </p>
          ) : null}
        </div>

        {/* ---- Question palette ---- */}
        <aside
          data-testid="test-palette"
          className="bg-white border border-slate-200 rounded-lg p-4 lg:sticky lg:top-6"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-900">Questions</h2>
            <span
              className="text-xs text-slate-500 tabular-nums"
              data-testid="test-palette-count"
            >
              {answeredCount}/{total}
            </span>
          </div>

          <div
            className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-5 gap-2"
            data-testid="test-palette-grid"
          >
            {questions.map((q, i) => {
              const isCurrent = i === index;
              const isAnswered = !!answers[q.id];
              const base =
                "h-9 w-full rounded-md text-xs font-semibold tabular-nums border transition focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-1";
              let style;
              if (isCurrent) {
                style =
                  "border-slate-900 bg-slate-900 text-white shadow-sm";
              } else if (isAnswered) {
                style =
                  "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100";
              } else {
                style =
                  "border-slate-300 bg-white text-slate-600 hover:bg-slate-100";
              }
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  data-testid={`test-palette-q-${i + 1}`}
                  data-state={
                    isCurrent ? "current" : isAnswered ? "answered" : "unanswered"
                  }
                  aria-current={isCurrent ? "true" : undefined}
                  aria-label={`Go to question ${i + 1}${
                    isAnswered ? ", answered" : ", not answered"
                  }`}
                  className={`${base} ${style}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          {/* legend */}
          <div className="mt-4 space-y-1.5 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm border border-slate-900 bg-slate-900" />
              Current
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm border border-emerald-300 bg-emerald-50" />
              Answered
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm border border-slate-300 bg-white" />
              Unanswered
            </div>
          </div>
        </aside>
      </div>
    </Shell>
  );
}

function Shell({ title, subtitle, children, wide = false }) {
  const navigate = useNavigate();
  const container = wide ? "max-w-5xl" : "max-w-2xl";
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className={`${container} mx-auto px-4 sm:px-6 py-4 flex items-center justify-between`}>
          <div>
            <h1
              className="text-base sm:text-lg font-semibold text-slate-900"
              data-testid="test-title"
            >
              {title}
            </h1>
            {subtitle ? (
              <p
                className="text-xs text-slate-500"
                data-testid="test-subtitle"
              >
                {subtitle}
              </p>
            ) : null}
          </div>
          <button
            onClick={() => navigate("/student")}
            className="text-xs text-slate-600 hover:text-slate-900"
          >
            Back to dashboard
          </button>
        </div>
      </header>
      <main className={`${container} mx-auto px-4 sm:px-6 py-6`}>{children}</main>
    </div>
  );
}
